import * as React from 'react';
// Import hooks.
import {
	useSubformPropertyInfo,
	UseSubformPropertyInfoParameters,
	UseSubformPropertyInfoResults,
} from '@app/hooks/view';
import {
	useInputDataSelectionList,
	UseInputDataSelectionListResults,
	useInputText,
	UseInputTextResults,
} from '@app/hooks/view/input';
// Import 
import {
	PropertyDomainValuesCommercialProps,
	SubformInputSettingsBuildingInfoProps,
} from '@app/props';
// Import api models.
import {
	BuildingRiskDataCommercial,
} from '@app/models/api';
// Import props.
import {
	SubformBuildingInfoProps,
} from '@app/props';

interface UseSubformBuildingInfoParameters extends UseSubformPropertyInfoParameters<PropertyDomainValuesCommercialProps, SubformInputSettingsBuildingInfoProps, BuildingRiskDataCommercial> {

}

interface UseSubformBuildingInfoResults {
	areAllInputsDisabled: boolean;
	propertyInfo: UseSubformPropertyInfoResults;
	buildingDescriptionInput: UseInputTextResults;
	parkingTypeInput: UseInputDataSelectionListResults;
	applySubformBuildingInfoProps: () => SubformBuildingInfoProps;
	createBuildingRiskDataCommercial: () => BuildingRiskDataCommercial;
	flagAllInputsToShowValidations: () => void;
	doInputsHaveChanges: () => boolean;
}

const useSubformBuildingInfo = (params: UseSubformBuildingInfoParameters): UseSubformBuildingInfoResults => {

	// #region exported methods

	const applySubformBuildingInfoProps = (): SubformBuildingInfoProps => {
		return {
			...propertyInfo.applySubformPropertyInfoProps(),
			showBuildingDescriptionInput,
			showParkingTypeInput,
			buildingDescriptionInput,
			parkingTypeInput,
		}
	}

	/**
	 *  Creates a building risk data commercial object.
	 */
	const createBuildingRiskDataCommercial = (): BuildingRiskDataCommercial => {
		let buildingRiskDataCommercial: BuildingRiskDataCommercial = {
			...propertyInfo.createPropertyInfoObject(),
			buildingDescription: buildingDescriptionInput.value ?? null,
			parkingType: parkingTypeInput.value ?? null,
		}

		return buildingRiskDataCommercial;
	}

	/**
	 * Flags all inputs to show the validation messages. 
	 **/
	const flagAllInputsToShowValidations = (): void => {
		propertyInfo.flagAllInputsToShowValidations();
		buildingDescriptionInput.changeShowValidationFlag(true);
		parkingTypeInput.changeShowValidationFlag(true);
	}

	/**
	 * Checks to see if the form has changes. 
	 **/
	const doInputsHaveChanges = (): boolean => {
		return propertyInfo.doInputsHaveChanges()
			|| buildingDescriptionInput.doesInputHaveChanges
			|| parkingTypeInput.doesInputHaveChanges;
	}

	// #endregion exported methods


	// #region private methods

	// #region id generators

	/**
	 * Generates id for the form. 
	 **/
	const generateIdForForm = (): string => {
		return params.id;
	}

	/**
	 * Generate id for the below grade or enclosed area finished. 
	 **/
	const generateIdForBuildingDescription = (): string => {
		return `${generateIdForForm()}-buildingDescription`;
	}

	/**
	 * Generate id for the parking type. 
	 **/
	const generateIdForParkingType = (): string => {
		return `${generateIdForForm()}-parkingType`;
	}

	// #endregion id generators

	// #endregion private methods


	// #region variables

	const showValidation: boolean = React.useMemo(() => params.showValidationsOnMount ?? false, [params.showValidationsOnMount]);

	// Input settings visibility.
	const showBuildingDescriptionInput: boolean = React.useMemo(() =>
		params.inputSettingsProps?.buildingDescriptionInput !== 'hidden',
		[params.inputSettingsProps?.buildingDescriptionInput]
	);
	const showParkingTypeInput: boolean = React.useMemo(() =>
		params.inputSettingsProps?.parkingTypeInput !== 'hidden',
		[params.inputSettingsProps?.parkingTypeInput]
	);

	// Input settings disable.
	const isBuildingDescriptionInputDisabled: boolean = React.useMemo(() =>
		!params.allowEdits
		|| !showBuildingDescriptionInput
		|| params.inputSettingsProps?.buildingDescriptionInput === 'disabled',
		[
			params.allowEdits,
			showBuildingDescriptionInput,
			params.inputSettingsProps?.buildingDescriptionInput,
		]
	);
	const isParkingTypeInputDisabled: boolean = React.useMemo(() =>
		!params.allowEdits
		|| !showParkingTypeInput
		|| params.inputSettingsProps?.parkingTypeInput === 'disabled',
		[
			params.allowEdits,
			showParkingTypeInput,
			params.inputSettingsProps?.parkingTypeInput,
		]
	);

	// Setup the basic property info hook.
	const propertyInfo: UseSubformPropertyInfoResults = useSubformPropertyInfo({
		...params,
		id: generateIdForForm(),
	});

	// Instantiate the 'building description' input hook.
	const buildingDescriptionInput: UseInputTextResults = useInputText({
		id: generateIdForBuildingDescription(),
		label: 'Building Description',
		required: true,
		disabled: isBuildingDescriptionInputDisabled,
		value: params.propertyInfo?.buildingDescription ?? String.empty(),
		showValidation,
	});

	// Setup the 'parking type' input hook.
	const parkingTypeInput: UseInputDataSelectionListResults = useInputDataSelectionList({
		id: generateIdForParkingType(),
		label: 'Parking Type',
		required: true,
		disabled: isParkingTypeInputDisabled,
		value: params.propertyInfo?.parkingType ?? undefined,
		possibleDataSelections: params.propertyDomainValuesProps.possibleParkingTypes,
		showValidation,
	});

	// Check to see if all inputs are disabled.
	const areAllInputsDisabled: boolean = React.useMemo(() => {
		return propertyInfo.areAllInputsDisabled
			&& buildingDescriptionInput.isDisabled
			&& parkingTypeInput.isDisabled;
	}, [
		propertyInfo.areAllInputsDisabled,
		buildingDescriptionInput.isDisabled,
		parkingTypeInput.isDisabled,
	]);

	// #endregion variables

	return {
		areAllInputsDisabled,
		propertyInfo,
		buildingDescriptionInput,
		parkingTypeInput,
		applySubformBuildingInfoProps,
		createBuildingRiskDataCommercial,
		flagAllInputsToShowValidations,
		doInputsHaveChanges,
	};
}

// Export parameters.
export {
	UseSubformBuildingInfoParameters,
}

// Export results.
export {
	UseSubformBuildingInfoResults,
}

export default useSubformBuildingInfo;